import styled, { keyframes } from 'styled-components';
import ReactDOM from 'react-dom';
import React, {
  createContext,
  DependencyList,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Styling } from '@/styling';
import { Backdrop } from '@/components/visual/Backdrop';
import { Box } from '@/components/layout/Box';

export type DialogProps = {
  onClose?(): void;
};

type DialogComponent = FC<DialogProps>;

export type DialogOptions = {
  /**
   * Determines whether the dialog window should be closed when the user clicks
   * outside of it.
   *
   * Defaults to `true` if not specified.
   */
  closeOnBackgroundClick?: boolean;
  /**
   * Determines whether the dialog window should be closed when the user presses
   * the Escape button.
   *
   * Defaults to `true` if not specified.
   */
  closeOnEscape?: boolean;
};

type Dialog = {
  component: DialogComponent;
  options: DialogOptions;
};

type DialogContext = {
  /**
   * The currently shown dialog, if any.
   */
  shownDialog: Dialog | null;
  /**
   * All the open dialogs (including the shown and background dialogs).
   */
  dialogs: Dialog[];
  /**
   * Queues up the dialog window to be shown.
   */
  enqueue(component: DialogComponent, options: DialogOptions): Dialog;
  /**
   * Dequeues the dialog window from being shown. Closes the window if it's already being shown.
   */
  dequeue(dialog: Dialog): void;
  /**
   * Updates the component of a dialog.
   */
  update(
    dialog: Dialog,
    component: DialogComponent,
    options: DialogOptions,
  ): void;
};

const DialogsContext = createContext<DialogContext | null>(null);

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const dialogRoot = document.getElementById('dialog-root')!;

const popUp = keyframes`
  from {
    transform: translateY(10px);
  }
  to {
    transform: translateY(0);
  }
`;

const FADE_DURATION_MS = 100;

const Scroller = styled.div`
  position: absolute;
  inset: 0;
  overflow-y: auto;
  z-index: ${Styling.zIndex('dialog')};
`;

const Inner = styled.div`
  min-height: 100%;
  display: grid;
  align-content: center;
  justify-content: center;
`;

const Container = styled(Box).attrs({
  direction: 'horizontal',
  alignX: 'center',
})<{ show: boolean }>`
  width: auto;
  opacity: ${({ show }) => (show ? 1 : 0)};
  animation: ${popUp} ${FADE_DURATION_MS}ms linear 1;
  transition: opacity ${FADE_DURATION_MS}ms;

  @media (max-width: ${Styling.breakpoint('md')}px) {
    width: 100%;
  }
`;

export const DialogProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const [dialogs, setDialogs] = useState<Dialog[]>([]);
  const shownDialog = useMemo(() => {
    if (dialogs.length === 0) {
      return null;
    }

    return dialogs[dialogs.length - 1];
  }, [dialogs]);
  const [isOpen, setIsOpen] = useState(false);
  const context = useMemo<DialogContext>(
    () => ({
      shownDialog,
      dialogs,
      enqueue(component, options) {
        const dialog = { component, options };

        setDialogs((dialogs) => [...dialogs, dialog]);

        return dialog;
      },
      dequeue(dialog) {
        const doClose = (): void => {
          setDialogs((dialogs) => {
            const copy = dialogs.slice();
            const index = copy.indexOf(dialog);

            if (index > -1) {
              copy.splice(index, 1);
              return copy;
            }

            return dialogs;
          });
        };

        if (dialogs.length <= 1) {
          doClose();
        } else {
          setIsOpen(false);
          setTimeout(doClose, FADE_DURATION_MS + 34);
        }
      },
      update(dialog, component, options) {
        setDialogs((dialogs) => {
          const copy = dialogs.slice();
          const index = copy.indexOf(dialog);

          if (index > -1) {
            // This is quite the hack. We update the array to trigger a re-render, and we mutate the object rather than
            // create a new one to maintain referential identity.
            copy[index].component = component;
            copy[index].options = options;
            return copy;
          }

          return dialogs;
        });
      },
    }),
    [shownDialog, dialogs],
  );

  useEffect(() => {
    setIsOpen(dialogs.length > 0);
  }, [dialogs]);

  useEffect(() => {
    const handleKeyDown = (evt: KeyboardEvent) => {
      if (
        evt.key === 'Escape' &&
        shownDialog &&
        shownDialog.options.closeOnEscape !== false
      ) {
        context.dequeue(shownDialog);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shownDialog]);

  const handleBackdropClick = () => {
    if (shownDialog && shownDialog.options.closeOnBackgroundClick !== false) {
      context.dequeue(shownDialog);
    }
  };

  return (
    <DialogsContext.Provider value={context}>
      {children}
      {ReactDOM.createPortal(
        <Backdrop show={dialogs.length > 0} durationMs={FADE_DURATION_MS}>
          <Scroller onClick={handleBackdropClick}>
            <Inner>
              <Container show={isOpen} onClick={(evt) => evt.stopPropagation()}>
                {shownDialog && (
                  <shownDialog.component
                    onClose={() => context.dequeue(shownDialog)}
                  />
                )}
              </Container>
            </Inner>
          </Scroller>
        </Backdrop>,
        dialogRoot,
      )}
    </DialogsContext.Provider>
  );
};

export type UseDialog<StaticProps> = {
  isOpen: boolean;
  isShown: boolean;
  open(props: StaticProps): void;
  close(): void;
};

export function useDialog<StaticProps = {}>(
  Component: FC<StaticProps & DialogProps>,
  options: DialogOptions,
  deps: DependencyList,
): UseDialog<StaticProps> {
  const context = useContext(DialogsContext);
  const dialog = useRef<Dialog>(undefined);
  const staticProps = useRef<StaticProps>(undefined);

  if (!context) {
    throw new Error('useDialog hook must be used within a DialogsProvider');
  }

  const isShown = dialog.current
    ? context.shownDialog === dialog.current
    : false;
  const isOpen = dialog.current
    ? context.dialogs.includes(dialog.current)
    : false;

  useEffect(() => {
    if (!isOpen) {
      dialog.current = undefined;
      staticProps.current = undefined;
    }
  }, [isOpen]);

  useEffect(() => {
    if (dialog.current && staticProps.current) {
      context.update(
        dialog.current,
        (dialogProps) => (
          <Component {...staticProps.current!} {...dialogProps} />
        ),
        options,
      );
    }
  }, deps);

  const open = (props: StaticProps) => {
    if (!dialog.current) {
      dialog.current = context.enqueue(
        (dialogProps) => <Component {...props} {...dialogProps} />,
        options,
      );
      staticProps.current = props;
    }
  };

  const close = () => {
    if (dialog.current) {
      context.dequeue(dialog.current);
    }
  };

  return { isOpen, isShown, open, close };
}

export type UseDialogs = {
  open(
    dialog: FC<DialogProps>,
    options?: DialogOptions,
  ): {
    close(): void;
  };
};

export function useDialogs(): UseDialogs {
  const context = useContext(DialogsContext);

  if (!context) {
    throw new Error('useDialogs hook must be used within a DialogsProvider');
  }

  return useMemo(
    () => ({
      open(dialog, options) {
        const handle = context.enqueue(dialog, {
          closeOnBackgroundClick: options?.closeOnBackgroundClick ?? true,
          closeOnEscape: options?.closeOnEscape ?? true,
        });

        return {
          close() {
            context.dequeue(handle);
          },
        };
      },
    }),
    [],
  );
}
